import React, { FC, PropsWithChildren, useState } from 'react';
import { useModal } from 'store/modal';
import API from 'core/Api';
import { FaTimes } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import { createErrorFromResponse } from 'core/Error';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';

interface IProps {
  leaveId: number;
  buttonText: string | null;
  handlePreview: (leaveId: number) => void;
  invalidate: () => void;
  iconOnly?: boolean;
  buttonInnerText?: string;
  buttonSize?: 'sm' | 'lg' | null;
  noTooltip?: boolean;
  wrapperClassName?: string;
  buttonClassName?: string;
  onBeforeOpenModal?: () => void;
}

interface IPropsReasonForm {
  handleReject?: any;
  leaveId: number;
}

interface IInputs {
  reason: string;
}

// Reject reason form
const ReasonForm: FC<IPropsReasonForm> = ({ handleReject, leaveId }: IPropsReasonForm) => {
  const methods = useForm<IInputs>();
  const { hideModal } = useModal();
  const { t } = useTranslation();
  return (
    <>
      <p className="text-center">{t('documents.table.confirmRejectReasonLabel')}</p>
      <Form
        methods={methods}
        onSubmit={(data: IInputs) => handleReject(leaveId, data)}
        btnLabel={t('documents.table.confirmRejectDocLabel')}
        primaryActionBtnType="danger"
        onCancel={() => hideModal()}
      >
        <FormGroup
          controlId="formGroupReason"
          as="textarea"
          name="reason"
        />
      </Form>
    </>
  );
};

const RejectLeave: FC<PropsWithChildren<IProps>> = ({
  leaveId,
  buttonText,
  handlePreview,
  invalidate,
  iconOnly,
  buttonInnerText,
  buttonSize = 'sm',
  noTooltip,
  wrapperClassName,
  buttonClassName,
  onBeforeOpenModal,
}: PropsWithChildren<IProps>) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const renderButtonContent = () => (
    <>
      <FaTimes size={14} />
      {buttonInnerText && <span>{buttonInnerText}</span>}
    </>
  );
  const wrapperClass = wrapperClassName ?? 'd-inline-block ml-2';

  // Reject document
  const handleReject = (id: number, data: any) =>
    API.post(`leaves/${id}/reject`, {
      reason: data.reason !== '' ? data.reason : null,
    })
      .then(() => {
        showModal({
          type: 'success',
          content: t('leaves.employer.rejectedLeaveSuccessMsg'),
          onSuccess: () => {},
        });
        invalidate();
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );

  // Confirm rejection
  const confirmReject = (id: number) => {
    if (onBeforeOpenModal) {
      onBeforeOpenModal();
    }

    showModal({
      type: 'form',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('leaves.employer.confirmRejectLeaveMsg')}</p>
          <Button
            type="button"
            btnType="primary"
            outlined
            disabled={isLoading}
            onClick={() => handlePreviewDoc({ showModal, id, setIsLoading })}
          >
            {t('documents.table.previewDoc')}
          </Button>
          <hr />
          <ReasonForm
            handleReject={handleReject}
            leaveId={id}
          />
        </>
      ),
    });
  };

  if (iconOnly) {
    const iconButton = (
      <Button
        type="button"
        btnType="icon"
        size="sm"
        ariaLabel={t('leaves.rejectLeave')}
        className={buttonClassName}
        onClick={() => confirmReject(leaveId)}
      >
        <FaTimes
          color="#EF476F"
          size={12}
        />
      </Button>
    );

    return (
      <div className={wrapperClass}>
        {noTooltip ? (
          iconButton
        ) : (
          <TooltipWrapper
            id="tooltip-reject"
            overlay={buttonText ?? ''}
          >
            {iconButton}
          </TooltipWrapper>
        )}
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {noTooltip ? (
        <Button
          type="button"
          btnType="danger"
          size={buttonSize === null ? undefined : buttonSize}
          className={buttonClassName}
          onClick={() => confirmReject(leaveId)}
        >
          {renderButtonContent()}
        </Button>
      ) : (
        <TooltipWrapper
          id="tooltip-reject"
          overlay={buttonText ?? ''}
        >
          <Button
            type="button"
            btnType="danger"
            size={buttonSize === null ? undefined : buttonSize}
            className={buttonClassName}
            onClick={() => confirmReject(leaveId)}
          >
            {renderButtonContent()}
          </Button>
        </TooltipWrapper>
      )}
    </div>
  );
};

export default RejectLeave;
